import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/utils/api';
import {
  Sparkles, Send, Loader2, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Lightbulb, Target, ArrowUpRight, ShieldAlert,
  ChevronDown, ChevronRight, Brain,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// ModuleIntelligenceBlock — Layer 1 (embedded) of REVA's dual-layer intelligence.
//
// Drops into any module workspace (Properties, Leases, Payments, Maintenance,
// Tenants, Vendors, Documents, Market) and surfaces:
//   • Headline KPI specific to this module
//   • Insights with severity tags
//   • Quantified recommendations
//   • Forecasts / risk callouts
//   • Decision shortcuts ("Open Decision Center")
//   • Ask REVA chat grounded in this module's context
//
// Props:
//   module  string  — "properties" | "leases" | "payments" | "maintenance" |
//                     "tenants" | "vendors" | "documents" | "intelligence"
//   testId  string  — root data-testid
// ─────────────────────────────────────────────────────────────────────────────

const sevStyle = (sev) => ({
  info:     { ring: 'border-[#E1E6EC] bg-white',                          dot: 'bg-[#9CA3AF]',                 text: 'text-[#4A5263]',   icon: Lightbulb },
  warn:     { ring: 'border-[rgba(212,175,55,0.35)] bg-[rgba(212,175,55,0.06)]', dot: 'bg-[#D4AF37]',           text: 'text-[#A07A0F]',   icon: AlertTriangle },
  critical: { ring: 'border-[rgba(220,38,38,0.30)] bg-[rgba(220,38,38,0.05)]',   dot: 'bg-[#DC2626]',           text: 'text-[#B91C1C]',   icon: ShieldAlert },
}[sev] || { ring: 'border-[#E1E6EC] bg-white', dot: 'bg-[#9CA3AF]', text: 'text-[#4A5263]', icon: Lightbulb });

const tierStyle = (tier) => ({
  excellent: { bg: 'bg-[rgba(0,168,107,0.08)]',  border: 'border-[rgba(0,168,107,0.30)]',  text: 'text-[#00A86B]' },
  good:      { bg: 'bg-[rgba(31,209,138,0.10)]', border: 'border-[rgba(31,209,138,0.35)]', text: 'text-[#0E8C5D]' },
  watch:     { bg: 'bg-[rgba(212,175,55,0.10)]', border: 'border-[rgba(212,175,55,0.40)]', text: 'text-[#A07A0F]' },
  critical:  { bg: 'bg-[rgba(220,38,38,0.06)]',  border: 'border-[rgba(220,38,38,0.30)]',  text: 'text-[#B91C1C]' },
  info:      { bg: 'bg-[#F7F8FA]',               border: 'border-[#E1E6EC]',              text: 'text-[#1A1F2A]' },
}[tier] || { bg: 'bg-[#F7F8FA]', border: 'border-[#E1E6EC]', text: 'text-[#1A1F2A]' });

const TrendIcon = ({ t }) => t === 'up' ? <TrendingUp className="h-3 w-3 text-[color:var(--reva-signal)]" /> : t === 'down' ? <TrendingDown className="h-3 w-3 text-red-600" /> : <Minus className="h-3 w-3 text-[#9CA3AF]" />;

export default function ModuleIntelligenceBlock({ module, testId = 'module-intel' }) {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [msgs, setMsgs] = useState([]);
  const [asking, setAsking] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.getModuleIntelligence(module);
        if (mounted) setData(r.data);
      } catch (e) {
        if (mounted) setData({ module, insights: [], recommendations: [], risks: [], forecasts: [], shortcuts: [] });
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [module]);

  const send = async (textOverride) => {
    const text = (textOverride ?? draft).trim();
    if (!text || asking) return;
    setMsgs(m => [...m, { role: 'user', content: text }]);
    setDraft('');
    setAsking(true);
    try {
      const grounded = data?.ask_context ? `[Context: ${data.ask_context}]\n\n${text}` : text;
      // Use the Decision Center's grounded ask endpoint — same backend, grounded in portfolio
      const r = await api.postDecisionsAsk(grounded, isAr ? 'ar' : 'en', { module });
      setMsgs(m => [...m, { role: 'assistant', content: r.data.answer || (isAr ? 'لا يوجد ردّ.' : 'No reply.') }]);
    } catch (e) {
      setMsgs(m => [...m, { role: 'assistant', content: isAr ? 'تعذّر الاتصال بـ REVA.' : 'Could not reach REVA.' }]);
    } finally { setAsking(false); }
  };

  if (loading) {
    return (
      <div className="reva-card-soft p-4 flex items-center gap-2 text-[12px] text-[#71777E]" data-testid={`${testId}-loading`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {isAr ? 'يحلّل REVA…' : 'REVA is analyzing…'}
      </div>
    );
  }

  if (!data) return null;
  const kpi = data.headline_kpi;
  const k = tierStyle(kpi?.tier);

  return (
    <div className="space-y-3" data-testid={testId}>
      {/* Header strip — module intelligence identity */}
      <div className="reva-card p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[color:var(--reva-signal)]/10 border border-[color:var(--reva-signal)]/25 flex items-center justify-center shrink-0">
            <Brain className="h-4 w-4 text-[color:var(--reva-signal)]" strokeWidth={1.7} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10.5px] reva-mono tracking-[0.16em] uppercase text-[color:var(--reva-signal)] font-semibold">
                {isAr ? 'ذكاء الوحدة · REVA' : 'Module intelligence · REVA'}
              </div>
              {kpi && (
                <span className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border ${k.bg} ${k.border} ${k.text}`} data-testid={`${testId}-kpi`}>
                  {isAr ? kpi.label_ar : kpi.label_en} · <span className="font-semibold">{kpi.value}</span>
                </span>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="text-[11px] text-[#71777E]"><span className="reva-mono">{data.insights?.length || 0}</span> {isAr ? 'رؤى' : 'insights'}</div>
              <div className="text-[11px] text-[#71777E]"><span className="reva-mono">{data.recommendations?.length || 0}</span> {isAr ? 'توصيات' : 'actions'}</div>
              <div className="text-[11px] text-[#71777E]"><span className="reva-mono">{data.risks?.length || 0}</span> {isAr ? 'مخاطر' : 'risks'}</div>
              <div className="text-[11px] text-[#71777E]"><span className="reva-mono">{data.forecasts?.length || 0}</span> {isAr ? 'توقّعات' : 'forecasts'}</div>
            </div>
          </div>
          {data.shortcuts?.length > 0 && (
            <button
              onClick={() => navigate(data.shortcuts[0].route)}
              className="reva-btn-ghost-l text-[11.5px] py-1.5 px-3 inline-flex items-center gap-1.5 shrink-0"
              data-testid={`${testId}-shortcut`}
            >
              {isAr ? data.shortcuts[0].label_ar : data.shortcuts[0].label_en}
              <ArrowUpRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {/* 3-column grid: insights · actions · risks-forecasts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* INSIGHTS */}
        <div className="reva-card p-4" data-testid={`${testId}-insights`}>
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
            <div className="text-[11px] reva-mono tracking-[0.16em] uppercase font-semibold text-[#0E1320]">
              {isAr ? 'رؤى مباشرة' : 'Live insights'}
            </div>
          </div>
          {data.insights?.length ? (
            <div className="space-y-2">
              {data.insights.map(ins => {
                const s = sevStyle(ins.severity);
                return (
                  <div key={ins.id} className={`rounded-lg border p-2.5 ${s.ring}`} data-testid={`${testId}-insight-${ins.id}`}>
                    <div className="flex items-start gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${s.dot} mt-1.5 shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-semibold text-[#0E1320]">{isAr ? ins.title_ar : ins.title_en}</div>
                        <div className="text-[11px] text-[#4A5263] mt-0.5 leading-relaxed">{isAr ? ins.body_ar : ins.body_en}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-[11.5px] text-[#9CA3AF]">{isAr ? 'لا توجد رؤى الآن' : 'No insights right now'}</div>
          )}
        </div>

        {/* RECOMMENDATIONS */}
        <div className="reva-card p-4" data-testid={`${testId}-recs`}>
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
            <div className="text-[11px] reva-mono tracking-[0.16em] uppercase font-semibold text-[#0E1320]">
              {isAr ? 'إجراءات موصى بها' : 'Recommended actions'}
            </div>
          </div>
          {data.recommendations?.length ? (
            <div className="space-y-2">
              {data.recommendations.map(r => (
                <div key={r.id} className="rounded-lg border border-[#ECEFF3] bg-[#FAFBFC] p-2.5" data-testid={`${testId}-rec-${r.id}`}>
                  <div className="text-[12px] font-semibold text-[#0E1320] leading-snug">{isAr ? r.title_ar : r.title_en}</div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold text-[color:var(--reva-signal)]">
                      {isAr ? r.expected_impact?.label_ar : r.expected_impact?.label_en}
                    </span>
                    <span className="text-[10px] text-[#71777E]">{Math.round((r.confidence || 0) * 100)}% {isAr ? 'ثقة' : 'conf'}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11.5px] text-[#9CA3AF]">{isAr ? 'لا توجد إجراءات الآن' : 'No actions right now'}</div>
          )}
        </div>

        {/* RISKS + FORECASTS */}
        <div className="reva-card p-4" data-testid={`${testId}-risks`}>
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="h-3.5 w-3.5 text-[#A07A0F]" />
            <div className="text-[11px] reva-mono tracking-[0.16em] uppercase font-semibold text-[#0E1320]">
              {isAr ? 'مخاطر وتوقّعات' : 'Risks & forecasts'}
            </div>
          </div>
          <div className="space-y-2">
            {(data.risks || []).map(r => {
              const s = sevStyle(r.severity);
              return (
                <div key={r.id} className={`rounded-lg border p-2.5 ${s.ring}`} data-testid={`${testId}-risk-${r.id}`}>
                  <div className="text-[12px] font-semibold text-[#0E1320] leading-snug">{isAr ? r.title_ar : r.title_en}</div>
                  <div className="text-[11px] text-[#4A5263] mt-0.5 leading-relaxed">{isAr ? r.body_ar : r.body_en}</div>
                </div>
              );
            })}
            {(data.forecasts || []).map((f, i) => (
              <div key={i} className="rounded-lg border border-[#ECEFF3] bg-white p-2.5 flex items-center gap-2" data-testid={`${testId}-forecast-${i}`}>
                <TrendIcon t={f.trend} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-[#71777E] truncate">{isAr ? f.metric_ar : f.metric_en}</div>
                  <div className="text-[12px] font-semibold text-[#0E1320]">{f.value_label_en}</div>
                </div>
              </div>
            ))}
            {(data.risks?.length || 0) + (data.forecasts?.length || 0) === 0 && (
              <div className="text-[11.5px] text-[#9CA3AF]">{isAr ? 'لا مخاطر بارزة' : 'No flagged risks'}</div>
            )}
          </div>
        </div>
      </div>

      {/* ASK REVA — module-aware */}
      <div className="reva-card-soft p-3" data-testid={`${testId}-ask`}>
        <button
          onClick={() => setChatOpen(o => !o)}
          className="w-full flex items-center gap-2 text-start"
          data-testid={`${testId}-ask-toggle`}
        >
          <Sparkles className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
          <div className="text-[11.5px] font-semibold text-[#0E1320] flex-1">
            {isAr ? 'اسأل REVA عن هذه الوحدة' : 'Ask REVA about this workspace'}
          </div>
          {chatOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#9CA3AF]" /> : <ChevronRight className="h-3.5 w-3.5 text-[#9CA3AF]" />}
        </button>
        {chatOpen && (
          <div className="mt-3 space-y-2">
            {msgs.length > 0 && (
              <div className="max-h-[180px] overflow-y-auto space-y-2 pr-1">
                {msgs.map((m, i) => (
                  <div key={i} className={`text-[12px] leading-relaxed rounded-lg px-3 py-2 ${m.role === 'user' ? 'bg-[color:var(--reva-signal)] text-white ms-auto max-w-[85%]' : 'bg-white border border-[#ECEFF3] text-[#0E1320]'}`}>
                    {m.role === 'user' && m.content.startsWith('[Context:') ? m.content.split('\n\n').slice(1).join('\n\n') : m.content}
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 rounded-full bg-white border border-[#E1E6EC] focus-within:border-[color:var(--reva-signal)] px-3 py-1.5 transition-colors">
              <input
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder={asking ? (isAr ? 'يفكر REVA…' : 'REVA is thinking…') : (isAr ? 'اطرح سؤالاً…' : 'Ask anything…')}
                disabled={asking}
                className="flex-1 bg-transparent text-[12px] text-[#0E1320] placeholder:text-[#9CA3AF] outline-none disabled:opacity-60"
                data-testid={`${testId}-ask-input`}
              />
              <button
                onClick={() => send()}
                disabled={asking || !draft.trim()}
                className="w-6 h-6 rounded-full bg-[color:var(--reva-signal)] hover:bg-[#00734A] text-white flex items-center justify-center transition-colors disabled:opacity-40"
                data-testid={`${testId}-ask-send`}
                aria-label="Send"
              >
                {asking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
